import { Tag } from "lucide-react";

import { SelectableOptionList } from "../../SelectableOptionList";
import { useChooseEntityVariant } from "../choose-entity-variant-context";

import useInfiniteScroll from "./hooks/useInfiniteScroll";
import { ReleaseLabel } from "./hooks/useReleaseLabels";

import { Entity } from "@/queries";

const ReleaseLabelsContent = ({
  isLoading,
  labels,
  selectedLabel,
  readonly,
  onLabelSelect,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  isLoading: boolean;
  labels: ReleaseLabel[];
  selectedLabel: string | null;
  readonly: boolean;
  onLabelSelect: (labelName: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}) => {
  const { entity, selectedEntities } = useChooseEntityVariant();
  const infiniteScrollRef = useInfiniteScroll(fetchNextPage, hasNextPage);

  // Get already selected labels for this entity (excluding the current one being edited)
  const alreadySelectedLabels = selectedEntities
    .filter((e: Entity) => (e.id === entity.id || e.name === entity.name) && e.label !== undefined)
    .map((e: Entity) => e.label);

  const isLabelDisabled = (label: ReleaseLabel): boolean =>
    alreadySelectedLabels.includes(label.name);

  return (
    <div className="h-full overflow-hidden">
      <SelectableOptionList<ReleaseLabel>
        isLoading={isLoading}
        loadingMessage="Loading release labels..."
        emptyStateTitle="No release labels available"
        emptyStateDescription="Create release labels to mark specific versions for deployment."
        items={labels}
        selectedKey={selectedLabel}
        readonly={readonly}
        getItemKey={label => label.name}
        // eslint-disable-next-line react/no-unstable-nested-components
        getItemLeading={() => <Tag className="h-4 w-4" aria-hidden />}
        // eslint-disable-next-line react/no-unstable-nested-components
        getItemLabel={label => <span className="font-medium text-gray-900">{label.name}</span>}
        getItemDescription={label => `Created ${new Date(label.created_at).toLocaleDateString()}`}
        isItemDisabled={isLabelDisabled}
        onItemSelect={label => onLabelSelect(label.name)}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        infiniteScrollRef={infiniteScrollRef}
      />
    </div>
  );
};

export default ReleaseLabelsContent;
